import React from "react";

import { Localizer } from "@/main/react/Types";
import { cn } from "@/lib/utils";
import { Inbox, ListPlus, MessageCircle, MessagesSquare, Quote, Share2, Users } from "lucide-react";
import { isBrowserOwnedClick } from "@/main/navigation/NavigationPolicy";

// Per-section result counts, populated from the discussion page bootstrap response. Counts for
// series/groups are supplied separately (they are already known client-side).
export type DiscussionSectionCounts = Partial<Record<string, number | null | undefined>>;

export type SectionDef = {
    value: string;
    label: string;
    icon: React.ElementType;
    count?: number;
};

/**
 * The section list, built once and read by BOTH renderers of it (`DISCUSSION-26`, 2026-08-24).
 *
 * <p>Below `md` the section strip is a plain block that scrolls away with the article and never comes
 * back, so `DiscussionMobileActionBar` — the only chrome that stays on screen there — now offers the same
 * sections from this same function. Exported rather than duplicated: two hand-kept copies of a list whose
 * membership depends on `canEdit`, `isDeleted`, the QUESTION facet and four separate counts is a list that
 * drifts, and the phone would be the copy nobody notices drifting.
 */
export function buildDiscussionSectionDefs(input: {
    localizer: Localizer;
    section?: string;
    repliesTabLabel: string;
    discussionGroupCount: number;
    discussionSeriesCount: number;
    canEdit: boolean;
    isDeleted: boolean;
    sectionCounts?: DiscussionSectionCounts;
}): SectionDef[] {
    const {
        localizer,
        section,
        repliesTabLabel,
        discussionGroupCount,
        discussionSeriesCount,
        canEdit,
        isDeleted,
        sectionCounts,
    } = input;

    /**
     * An empty Groups or Series tab is hidden from READERS and kept for the AUTHOR (2026-08-30, D3).
     *
     * <p>⚠️ The `|| canEdit` half is the whole reason this is not `count > 0`, and it is not
     * defensive: the empty state inside each panel is where
     * `showAddDiscussionToGroupsDialog` / `showAddDiscussionToSeriesDialog` are offered
     * (`DiscussionPage.tsx`), and an empty state is only rendered by VISITING the tab. Hide the tab
     * outright and a post in zero groups loses the add path that sits inside the surface its author
     * is already looking at. It mirrors the `requests` tab below, which is already author-gated.
     *
     * <p>Stated plainly, because it is the honest consequence: the tab reduction helps readers only.
     * On their own post an author still sees today's full strip. Pinned by
     * `DiscussionPageSectionControls.spec.tsx` — neutralise the `|| canEdit` half and that case goes
     * red, which is the check a rule with an untested exemption never survives.
     */
    const showGroups = discussionGroupCount > 0 || canEdit;
    const showSeries = discussionSeriesCount > 0 || canEdit;
    const quotedByCount = normalizeCount(sectionCounts?.["quoted-by"]) ?? 0;

    const defs: SectionDef[] = [
        // `DISCUSSION-64` — the focused-thought state is labelled with the SIBLING state's word, not
        // with the storage term. `discussion.section.opening` ("Opening") is `openingArgumentPublicId`
        // / `DiscussionOpeningThoughtOrdering` surfaced to members (AGENTS.md rule 12), and it reads as
        // "the post's opening text", which is not what the panel below contains: on this section the
        // list is a single focused thread (`DiscussionPage.tsx`, the "opening" branch says so in its
        // own words). The sibling `section === "conversation"` state already labels its tab
        // "Conversation" and the register row names it as the case that does NOT have this problem, so
        // this REUSES that key rather than minting one (rule 11) — the two entries are mutually
        // exclusive by construction, so no screen ever shows two tabs reading "Conversation". The
        // `BookOpen` glyph moves with it: a book icon was the other half of the same wrong claim.
        //
        // ⚠️ NOT taken here, deliberately: whether the state should ALSO be named above the chain
        // itself is that row's own "product call, not a copy swap", and it is parked.
        ...(section === "opening"
            ? [{ value: "opening", label: localizer("discussion.section.conversation"), icon: MessageCircle }]
            : []),
        ...(section === "conversation"
            ? [{ value: "conversation", label: localizer("discussion.section.conversation"), icon: MessageCircle }]
            : []),
        { value: "conversations", label: repliesTabLabel, icon: MessagesSquare, count: normalizeCount(sectionCounts?.conversations) },
        { value: "participants", label: localizer("discussion.section.participants"), icon: Users, count: normalizeCount(sectionCounts?.participants) },
        // GROUPS before RELATED, and SERIES last (2026-08-30, D3). The strip is one horizontally
        // scrollable row and at 412px it clips after the third entry, so the order is a statement
        // about what a phone reader actually gets to see: the post's own affiliations before the
        // site's suggestions about it.
        ...(showGroups
            ? [{ value: "groups", label: localizer("discussion.section.groups"), icon: Users, count: normalizeCount(discussionGroupCount) }]
            : []),
        // No count on Related, DELIBERATELY (`DISCUSSION-9`, wave-6 batch H). It is not an
        // oversight and it is not waiting on a wiring fix: no `relatedCount` exists anywhere in
        // the product. Related posts are served from a snapshot table by
        // `DiscussionRelationshipFacade.findRelatedDiscussions`, which has no count method, so
        // producing one means a new `countRelatedDiscussions` on that facade and its repository
        // plus an extra count query on both delivery paths of the busiest page in the product.
        // Until someone decides that trade is worth making, binding an always-`undefined` field
        // here just made the blank look like a bug. `sectionCounts.related` and the
        // `relatedCount` DTO field are kept so that decision stays a thread-through rather than
        // a redesign.
        { value: "related", label: localizer("discussion.section.related"), icon: Share2 },
        // "Quoted by" (quote post, design §10.3). Hidden at zero, with NO `|| canEdit` half — and that
        // asymmetry against groups/series is deliberate. Their empty tabs are kept for the author
        // because the empty state is where the "add to a group / add to a series" path lives; there is
        // no equivalent here. An author cannot make somebody quote them, so an empty "Quoted by" tab
        // would be a permanent reminder that nobody has, on their own post, offering nothing to do
        // about it.
        //
        // The count IS bound, unlike Related's: it exists on both delivery paths (the page-data
        // response and the JTE data attribute), so the badge is a real number rather than an
        // always-undefined field.
        ...(quotedByCount > 0
            ? [{ value: "quoted-by", label: localizer("discussion.section.quoted_by"), icon: Quote, count: normalizeCount(quotedByCount) }]
            : []),
        ...(showSeries
            ? [{ value: "series", label: localizer("discussion.section.series"), icon: ListPlus, count: normalizeCount(discussionSeriesCount) }]
            : []),
    ];

    if (canEdit && !isDeleted) {
        defs.push({ value: "requests", label: localizer("discussion.section.requests"), icon: Inbox, count: normalizeCount(sectionCounts?.requests) });
    }

    return defs;
}

/**
 * The replies tab's label, which is also the section row's accessible name so the two never disagree.
 *
 * <p>Exported for the same reason as the builder above: the mobile bar's section control lists the same
 * sections, and the QUESTION facet renames exactly this one.
 */
export function resolveRepliesTabLabel(localizer: Localizer, isQuestion: boolean): string {
    return localizer(isQuestion
        ? "discussion.question.answers.heading"
        : "discussion.section.conversations");
}

// `DISCUSSION-14`: the section controls own real URLs, so a reader who wants the participant roster
// in a second tab must be able to have it. The predicate lives in
// `main/navigation/NavigationPolicy.ts` since 2026-09-07 (`FA-R2`).

const COUNT_CAP = 999;

function formatCount(count: number): string {
    return count > COUNT_CAP ? `${COUNT_CAP}+` : String(count);
}

function normalizeCount(value: number | null | undefined): number | undefined {
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * The per-section count, as the strip renders it.
 *
 * <p>EXPORTED for `DiscussionMobileActionBar`'s section menu (`DISCUSSION-55`): the phone's menu had no
 * count at all, and the fix must not be a second formatter. The mobile navigation primitive takes a
 * trailing SLOT rather than a `count: number`, precisely so the cap (`COUNT_CAP`), the formatting and
 * the active/inactive treatment stay here, in the one place the strip already renders them.
 */
export function SectionCountBadge({ count, active }: { count?: number; active?: boolean }) {
    if (count == undefined) {
        return null;
    }
    return (
        <span
            className={cn(
                "rounded-full px-1.5 py-0.5 text-[0.6875rem] font-semibold leading-none tabular-nums",
                active
                    ? "bg-primary/10 text-foreground"
                    : "bg-muted text-muted-foreground",
            )}
        >
            {formatCount(count)}
        </span>
    );
}

/**
 * The discussion page's section row.
 *
 * It used to carry the thought-list group filter and ordering select underneath the tabs as well. Those
 * moved down to `DiscussionThoughtListControls`, rendered by `DiscussionThoughtsDisplayer` directly above
 * the list they order (2026-07-30) — a control has to sit next to what it changes, and up here it read as
 * applying to the section rather than to the list. Only `discussionGroupCount` survived the move, because
 * that one feeds the Groups badge, not the filter.
 *
 * NAVIGATION, NOT TABS (`DISCUSSION-14`, 2026-08-14). This row used to declare `role="tablist"` with
 * `role="tab"` children while nothing on the discussion surface declared `role="tabpanel"` and no
 * control carried `aria-controls` — a screen reader was told "tab, 2 of 5, selected" and then had no
 * panel to move to. Meanwhile every control changes the URL. So the honest shape is what is here now:
 * a `<nav>` of real anchors with `aria-current="page"`, plain clicks routed through the page's own
 * history-backed state and modified clicks left to the browser. Design §13.3.
 */
export default function DiscussionPageSectionControls(props: {
    localizer: Localizer;
    section?: string;
    onSectionChanged?: (section: string) => void;
    /**
     * The real address of a section, from the page's own URL builder. Optional so the component
     * still renders (as buttons) for a caller that has no slug to build one from — the SPA behaviour
     * is identical either way, and only the browser-owned affordances depend on the href.
     */
    buildSectionHref?: (section: string) => string | undefined;
    discussionGroupCount?: number;
    discussionSeriesCount?: number;
    canEdit?: boolean;
    isDeleted?: boolean;
    sectionCounts?: DiscussionSectionCounts;
    sticky?: boolean;
    /**
     * QUESTION facet: the replies tab reads "Answers" instead of "Conversations".
     *
     * This tab IS the island's section heading. `discussion.jte` server-renders an
     * `<h2>Answers</h2>` — but inside the `data-component` container, so `createRoot()` discards it on
     * mount and there is no React `<h2>` to swap. Rule 32 is about what survives hydration (crawlers
     * render), so relabelling the tab is what keeps a question reading as a question on the one surface
     * the facet is named for. Design §11.1, and the M8 status block for why it is the tab, not an `<h2>`.
     */
    isQuestion?: boolean;
}) {
    const {
        localizer,
        section,
        onSectionChanged,
        buildSectionHref,
        discussionGroupCount = 0,
        discussionSeriesCount = 0,
        canEdit = false,
        isDeleted = false,
        sectionCounts,
        sticky = true,
        isQuestion = false,
    } = props;

    // The replies tab's label, reused as the tab row's accessible name so the two never disagree.
    const repliesTabLabel = resolveRepliesTabLabel(localizer, isQuestion);

    const sectionDefs = React.useMemo<SectionDef[]>(
        () => buildDiscussionSectionDefs({
            localizer,
            section,
            repliesTabLabel,
            discussionGroupCount,
            discussionSeriesCount,
            canEdit,
            isDeleted,
            sectionCounts,
        }),
        [canEdit, discussionGroupCount, discussionSeriesCount, isDeleted, localizer, repliesTabLabel, section, sectionCounts],
    );

    // No roving tabindex and no arrow-key handler: those are the ARIA tabs pattern's conventions, and
    // this row is navigation (`DISCUSSION-14`). Links in a nav are reached with Tab, one stop each.

    // Does the section row actually overflow? The row hides its scrollbar, so a clipped tab reads as
    // broken text ("Relc") rather than as "scroll for more" — but a fade painted when everything fits
    // is its own defect, dimming a flush final tab for no reason. So measure, and only then mask.
    // The state flips only when the answer changes, so a resize storm cannot churn renders.
    const tabListRef = React.useRef<HTMLElement | null>(null);
    const [tabsOverflow, setTabsOverflow] = React.useState(false);
    React.useEffect(() => {
        const node = tabListRef.current;
        if (!node || typeof ResizeObserver === "undefined") {
            return;
        }
        const measure = () => {
            const overflowing = node.scrollWidth > node.clientWidth + 1;
            setTabsOverflow((previous) => (previous === overflowing ? previous : overflowing));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [sectionDefs.length]);

    return (
        <div
            className={cn(
                "flex w-full min-w-0 flex-col gap-2",
                // Sticky only on desktop; offset below the app header (and connection banner, if
                // shown). Uses the page background so it stays subtle (not a colored bar) while
                // still masking content scrolling underneath it.
                sticky ? "md:sticky md:top-[calc(4rem_+_var(--seavien-connection-banner-height,0px))] md:z-layer-sticky md:bg-[var(--background1)]" : undefined,
            )}
            data-playwright-id="discussion-page-section-controls"
        >
            {/* The section row: a single, horizontally scrollable row of links with a subtle underline
                indicator (no background fill), like the search tabs.

                The row hides its scrollbar (`no-scrollbar`), so at narrow widths a clipped entry used to
                read as broken text rather than as "there is more": at 412px the third one rendered as
                "Relc" hard against the viewport edge with Series and Groups entirely off-screen. The
                right-edge mask fades the last ~2rem so a partially visible entry reads as continuation.
                Gated on a measured overflow, not on a breakpoint — a fade over a row that fits would
                dim its final entry for no reason. (2026-07-26 ux-refine run.) */}
            <nav
                ref={tabListRef}
                aria-label={localizer("discussion.sections.nav.label")}
                className={cn(
                    "no-scrollbar flex w-full items-center gap-1 overflow-x-auto border-b border-border",
                    tabsOverflow && "[mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent_100%)]",
                )}
                data-playwright-id="discussion-page-section-tabs"
                style={{ WebkitOverflowScrolling: "touch" }}
            >
                {sectionDefs.map((def) => {
                    const isActive = def.value === section;
                    const Icon = def.icon;
                    const href = buildSectionHref?.(def.value);
                    return (
                        <a
                            key={def.value}
                            href={href}
                            aria-current={isActive ? "page" : undefined}
                            data-playwright-id={`discussion-section-tab-${def.value}`}
                            onClick={(event) => {
                                // A modified or middle click is the reader asking the BROWSER for this
                                // URL. Leave it alone — intercepting it is the half of `DISCUSSION-14`
                                // that an href alone would not fix.
                                if (isBrowserOwnedClick(event)) {
                                    return;
                                }
                                event.preventDefault();
                                onSectionChanged?.(def.value);
                            }}
                            className={cn(
                                // No `-mb-px` here on purpose: it shortened the flex line by 1px, and
                                // because the row is `overflow-x-auto` (which computes `overflow-y` to
                                // `auto`, clipping to the padding box) that put the whole indicator
                                // below the clip edge. `DISCUSSION-2`: it never painted, in any theme
                                // or viewport, since the day it was written.
                                "relative flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors",
                                "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                                "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                                isActive
                                    ? "text-foreground"
                                    : "text-muted-foreground hover:bg-muted",
                            )}
                        >
                            <Icon className="size-4" aria-hidden="true" />
                            <span>{def.label}</span>
                            <SectionCountBadge count={def.count} active={isActive} />

                            {/* Animated active underline: draws in from the center on activation.
                                `bottom-0` keeps it inside the row's clip box; see the class note above. */}
                            <span
                                aria-hidden="true"
                                data-playwright-id="discussion-section-tab-indicator"
                                data-active={isActive ? "true" : "false"}
                                className={cn(
                                    "pointer-events-none absolute inset-x-2 bottom-0 h-0.5 origin-center rounded-full bg-primary transition-transform duration-200 ease-out",
                                    isActive ? "scale-x-100" : "scale-x-0",
                                )}
                            />
                        </a>
                    );
                })}
            </nav>
        </div>
    );
}
